import React from 'react'
import { Message } from '../types'
import { ArchiveBoxIcon, ChevronDownIcon } from '@heroicons/react/24/outline'

/**
 * Props của component MessageContent
 */
interface MessageContentProps {
  /** Danh sách tin nhắn đang xử lý */
  processingMessages: Message[]
  /** Danh sách tin nhắn đã hoàn thành */
  completedMessages: Message[]
  /** Trạng thái hiển thị tin nhắn đã hoàn thành */
  showCompleted: boolean
  /** Hàm cập nhật trạng thái hiển thị tin nhắn đã hoàn thành */
  setShowCompleted: (show: boolean) => void
  /** Hàm xử lý khi click vào tin nhắn khách hàng */
  handleCustomerChat: (message: Message) => void
  /** Hàm xử lý khi click vào tin nhắn khác */
  handleCardClick: (message: Message) => void
}

/**
 * Component hiển thị nội dung tin nhắn
 * 
 * Cấu trúc:
 * 1. Danh sách tin nhắn đang xử lý
 * 2. Phần tin nhắn đã hoàn thành (có thể ẩn/hiện)
 * 
 * Chức năng:
 * - Hiển thị danh sách tin nhắn đang xử lý
 * - Hiển thị danh sách tin nhắn đã hoàn thành
 * - Xử lý click vào tin nhắn
 * - Toggle hiển thị tin nhắn đã hoàn thành
 */
export const MessageContent: React.FC<MessageContentProps> = ({
  processingMessages,
  completedMessages,
  showCompleted,
  setShowCompleted,
  handleCustomerChat,
  handleCardClick
}) => {
  return (
    <div className="flex-1 overflow-y-auto">
      {/* Danh sách tin nhắn đang xử lý */}
      <div>
        {processingMessages.map((message) => (
          <div 
            key={message.id}
            onClick={() => {
              if (message.type === 'customer' || message.type === 'customer_group') {
                handleCustomerChat(message)
              } else {
                handleCardClick(message)
              }
            }}
            className="cursor-pointer"
          >
            <div className="flex items-start space-x-4 p-4 hover:bg-gray-50 bg-white">
              <div className={`flex-shrink-0 w-10 h-10 ${message.iconBg} rounded-full flex items-center justify-center`}>
                <message.icon className={`w-6 h-6 ${message.iconColor}`} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <h3 className={`text-sm font-medium truncate ${message.unread ? 'text-blue-600' : 'text-gray-900'}`}>
                    {message.title}
                  </h3>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                    {message.unread && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full" />
                    )}
                    {(message.important || message.warning) && (
                      <div className={`w-2 h-2 rounded-full ${message.important ? 'bg-red-500' : 'bg-yellow-500'}`} />
                    )}
                    <span className="text-xs text-gray-500">{message.time}</span>
                  </div>
                </div>
                <p className={`text-sm ${message.unread ? 'text-gray-900' : 'text-gray-500'} mt-1 line-clamp-2`}>
                  {message.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Phần tin nhắn đã hoàn thành */}
      <div className="mt-4 mb-24">
        <button
          onClick={() => setShowCompleted(!showCompleted)}
          className="w-full px-4 py-3 flex items-center justify-between text-sm text-gray-600 bg-gray-50 hover:bg-gray-100"
        >
          <div className="flex items-center space-x-2">
            <ArchiveBoxIcon className="w-5 h-5" />
            <span>Đã hoàn thành ({completedMessages.length})</span>
          </div>
          <ChevronDownIcon className={`w-5 h-5 transform transition-transform ${showCompleted ? 'rotate-180' : ''}`} />
        </button>

        {showCompleted && (
          <div>
            {completedMessages.map((message) => (
              <div 
                key={message.id}
                onClick={() => {
                  if (message.type === 'customer' || message.type === 'customer_group') {
                    handleCustomerChat(message)
                  } else {
                    handleCardClick(message)
                  }
                }}
                className="cursor-pointer opacity-75"
              >
                <div className="flex items-start space-x-4 p-4 hover:bg-gray-50 bg-white">
                  <div className={`flex-shrink-0 w-10 h-10 ${message.iconBg} rounded-full flex items-center justify-center`}>
                    <message.icon className={`w-6 h-6 ${message.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-gray-600 truncate">
                        {message.title}
                      </h3>
                      <span className="text-xs text-gray-500 ml-2 flex-shrink-0">{message.time}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {message.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
} 